import { useEffect, useState } from 'react';

type UseDebounceProps<T extends string | number> = {
  value: T;
  delay?: number;
  callback?: (value: T extends string ? string : number) => void;
};

function useDebounce<T extends string | number>({
  value,
  delay = 500,
  callback,
}: UseDebounceProps<T>) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [isChanging, setIsChanging] = useState(false);
  useEffect(() => {
    const isEmptyString = value === '';
    const handler = setTimeout(
      () => {
        setDebouncedValue(value);
        setIsChanging(false);
      },
      isEmptyString ? 0 : delay
    );

    return () => {
      setIsChanging(true);
      clearTimeout(handler);
    };
  }, [value, delay, callback]);

  useEffect(() => {
    callback &&
      callback(debouncedValue as unknown as T extends string ? string : number);
  }, [debouncedValue, callback]);

  return { debouncedValue, isChanging };
}

export default useDebounce;
